<template>
  <div class="child" :style="{ height: lineheight + 'px', lineHeight: lineheight + 'px' }">
    <div @contextmenu.prevent="openPartContextMenu" class="childTitle partTitle">
      <span v-if="draggingAllowed" :class="{ 'glyphicon-move': draggingAllowed }" aria-hidden="true">&#8597;</span>
      <span v-if="!draggingAllowed" class="glyphicon-no-move" aria-hidden="true">&nbsp;</span>
      <input
        :readonly="!editingAllowed || !hasPropertyPermissions('Part', 'title')"
        :value="part.title"
        @change="updateTitle"
        @drop.prevent=""
        @focus="focusOnTitle"
        @copy="copy"
        @keyup.ctrl.enter.stop.prevent="embedPart"
        @keyup.shift.enter="addPartSibling"
        @paste="paste"
        class="childTitle"
        ref="title"
        type="text"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { EventBus } from '@/common/eventbus';

export default {
  name: 'PartTitle',
  data() {
    return {
      panStart: moment(),
      panType: '',
      lock: false,
    };
  },
  props: ['filterDate', 'filterDateZoom', 'level', 'lineheight', 'lockedDragNDrop', 'part', 'stage'],
  watch: {},
  methods: {
    ...mapActions([
      'addPart',
      'copyChildNode',
      'delayedSelection',
      'embedPartInStage',
      'pasteChildNode',
      'savePartProperties',
    ]),
    copy: async function (e) {
      var copy = await this.copyChildNode({ node: this.part });
      e.clipboardData.setData('text/plain', this.part.title);
      e.clipboardData.setData('application/json', JSON.stringify(copy, ['title', 'children'], ' '));
      e.clipboardData.setData('application/boon-object', JSON.stringify(copy));
      e.preventDefault();
    },
    paste: function (e) {
      var nodeText = e.clipboardData.getData('application/boon-object');
      if (nodeText) {
        this.pasteChildNode({
          pasteOnNode: this.part,
          pasteNode: JSON.parse(nodeText),
          startdate: this.part.startdate,
        });
        e.preventDefault();
      }
    },
    openPartContextMenu: function (e) {
      if (this.hasSimplePermissions('deletePart') || this.hasSimplePermissions('embedPartInStage')) {
        EventBus.$emit('openPartContextMenu', { event: e, data: { part: this.part } });
      }
    },
    focusOnTitle: function () {
      if (this.hasSimplePermissions('selectPart')) {
        this.delayedSelection({ part: this.part });
      }
      this.$refs['title'].focus();
      if (this.hasPropertyPermissions('Part', 'title')) {
        this.$refs['title'].select();
      }
    },
    getLock: function () {
      if (this.lock) {
        return false;
      }
      this.lock = true;
      return true;
    },
    embedPart: function (e) {
      this.updateTitle(e);
      this.embedPartInStage({ part: this.part }).then((newPart) => {
        EventBus.$emit('focusPart', newPart);
      });
    },
    addPartSibling: function (e) {
      if (e.ctrlKey) {
        return;
      }
      if (!this.getLock()) {
        return;
      }
      this.updateTitle(e);
      this.addPart({
        siblingPart: this.part._id,
        newPartProperties: { startdate: moment(this.part.startdate).format('YYYY-MM-DD'), colour: this.part.colour },
      }).then((newPart) => {
        EventBus.$emit('focusPart', newPart);
        this.lock = false;
        return newPart;
      });
    },
    updateTitle: function (e) {
      return new Promise((resolve, reject) => {
        if (this.part.title !== e.target.value) {
          resolve(this.savePartProperties({ updatePart: this.part, newPartProperties: { title: e.target.value } }));
        }
        resolve(this.part);
      }).then((part) => {
        return part;
      });
    },
  },
  computed: {
    ...mapGetters([
      'currentProject',
      'editingAllowed',
      'hasSimplePermissions',
      'projectDraggingAllowed',
      'hasPropertyPermissions',
      'selectedPart',
      'viewSettings',
    ]),
    draggingAllowed: function () {
      return !this.lockedDragNDrop && this.projectDraggingAllowed;
    },
    paddingLeft: function () {
      return this.level * 10 + 'px';
    },
    titleWidth: function () {
      return Math.max(155 - this.level * 10, 30) + 'px';
    },
    backgroundPos: function () {
      var day = new Date(this.filterDate).getDay() - 1;
      if (day < 1) {
        day += 7;
      }
      var diff = this.filterDateZoom * this.viewSettings.projectPlan.zoomFactor * -day;
      return diff + 'px';
    },
    backgroundSize: function () {
      var size = (70 * this.filterDateZoom * this.viewSettings.projectPlan.zoomFactor) / 10;
      return size + 'px';
    },
    leftPos: function () {
      var leftPos = this.left;
      if (leftPos < 0) {
        leftPos = 0;
      }

      return leftPos + 'px';
    },
    left: function () {
      var start = moment(this.part.startdate);
      var first = moment(this.filterDate);
      var left =
        (this.filterDateZoom * this.viewSettings.projectPlan.zoomFactor * (start - first)) / (1000 * 60 * 60 * 24);

      return left;
    },
    width: function () {
      var left = this.left;
      var width =
        this.part.calendarTime * this.filterDateZoom * this.viewSettings.projectPlan.zoomFactor + Math.min(0, left);
      if (width < 10) {
        width = 10;
      }
      return width + 'px';
    },
  },
  components: {},
  mounted: function () {
    EventBus.$on('focusPart', (part) => {
      if (part && part._id === this.part._id) {
        this.focusOnTitle();
      }
    });
  },
  beforeDestroy() {
    EventBus.$off('focusPart');
  },
};
</script>

<style scoped>
.child {
  border-bottom: 1px solid black;
  padding-left: 38px;
}

input {
  vertical-align: top;
  background-color: inherit;
  border: 0px;
  color: white;
}
/* This is to fix css load order diff between dev and build. */
input:read-only {
  color: #888;
}

.part {
  background: white;
  text-align: left;

  position: relative;
  border: 1px solid grey;
}

.partBar.selected {
  background-color: red;
}

.childTitle {
  position: relative;
  white-space: nowrap;
  display: inline-block;
  z-index: 100;
  overflow: hidden;
  font-size: 12px;
}

.childTitle:focus {
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/projectplan/PartTitle.vue